import React from 'react';
import { DraftNumberInput } from '../../../../shared/components/DraftNumberInput';

/**
 * Slide-over drawer for the PipeFitter rates, prefilled from the active config
 * and overridable per proposal. Same pattern as the Material Traceability and
 * platform drawers. The scan margin is stored as a fraction but shown as a
 * percent; everything else is a plain rate or fee.
 */

const PERCENT = 100;

export const PIPEFITTER_RATE_FALLBACKS: Record<string, number> = {
  technicianDayRate: 800,
  scannerDayCost: 150,
  pipesPerDay: 300,
  scanMargin: 0.5,
  mobilization: 10000,
  byodNreFee: 15000,
  pipeDataPerJoint: 4,
  pipeCompatibilityPerJoint: 2,
  optiFitPerJoint: 10,
  omniFitPerJoint: 15,
  captureShare: 0.1,
  gainShareFloor: 5,
  optiFitCap: 30,
  omniFitCap: 50,
  moduleFloor: 20000,
};

// Onboarding (NRE) rates live in the shared nre config and are overridden with
// an nre. prefix. The format difficulty multipliers are nested under it.
export const PIPEFITTER_NRE_FALLBACKS: Record<string, number> = {
  setupHours: 8,
  perUnitHours: 2,
  validationOverhead: 0.25,
  engineerRate: 100,
  marginTarget: 0.75,
};
const DIFFICULTY_FALLBACKS: Record<string, number> = {
  api: 0.5,
  spreadsheet: 1,
  scannedPdf: 2,
  handwritten: 3,
};

interface RateField {
  key: string;
  label: string;
  percent?: boolean;
  step?: number;
  tooltip: string;
  nre?: boolean;
}

interface Group {
  title: string;
  fields: RateField[];
}

const GROUPS: Group[] = [
  {
    title: 'Scanning (CRC service, per pipe)',
    fields: [
      { key: 'technicianDayRate', label: 'Technician $/day', tooltip: 'Day rate for one scanning technician.' },
      { key: 'scannerDayCost', label: 'Scanner $/day', tooltip: 'Daily cost of the scanner hardware.' },
      { key: 'pipesPerDay', label: 'Pipes / day', tooltip: 'Scanning throughput; higher lowers the per-pipe cost.' },
      { key: 'scanMargin', label: 'Scan margin %', percent: true, step: 1, tooltip: 'Profit share on the scanning cost.' },
      { key: 'mobilization', label: 'Mobilization (1x)', tooltip: 'One-time mobilization fee for the scanning crew.' },
      { key: 'byodNreFee', label: 'BYOD onboarding NRE', tooltip: 'One-time onboarding fee when scanning is off (BYOD).' },
    ],
  },
  {
    title: 'Reporting (per joint)',
    fields: [
      { key: 'pipeDataPerJoint', label: 'Pipe Data $/joint', tooltip: 'Per-joint rate for the Pipe Data report.' },
      { key: 'pipeCompatibilityPerJoint', label: 'Compatibility $/joint', tooltip: 'Per-joint rate for the Pipe Compatibility report.' },
    ],
  },
  {
    title: 'Algorithm (per joint)',
    fields: [
      { key: 'optiFitPerJoint', label: 'OptiFit $/joint', tooltip: 'Per-joint rate for the OptiFit algorithm.' },
      { key: 'omniFitPerJoint', label: 'OmniFit $/joint', tooltip: 'Per-joint rate for the OmniFit algorithm.' },
    ],
  },
  {
    title: 'Algorithm gain-share',
    fields: [
      { key: 'captureShare', label: 'Capture share %', percent: true, step: 1, tooltip: 'Share of the proven fit-up saving we charge.' },
      { key: 'gainShareFloor', label: 'Floor $/joint', tooltip: 'Minimum per-joint gain-share price.' },
      { key: 'optiFitCap', label: 'OptiFit cap $/joint', tooltip: 'Maximum per-joint gain-share price for OptiFit.' },
      { key: 'omniFitCap', label: 'OmniFit cap $/joint', tooltip: 'Maximum per-joint gain-share price for OmniFit.' },
    ],
  },
  {
    title: 'BYOD onboarding (NRE) rates',
    fields: [
      { key: 'setupHours', label: 'Setup hours', nre: true, tooltip: 'Base engineering hours to onboard BYOD data.' },
      { key: 'perUnitHours', label: 'Hours / unit', nre: true, tooltip: 'Build hours per ingestion unit, scaled by format difficulty.' },
      { key: 'validationOverhead', label: 'Validation overhead %', nre: true, percent: true, step: 1, tooltip: 'Validation overhead added to build hours.' },
      { key: 'engineerRate', label: 'Engineer $/hr', nre: true, tooltip: 'Onboarding engineer hourly rate.' },
      { key: 'marginTarget', label: 'NRE margin %', nre: true, percent: true, step: 1, tooltip: 'Target margin on the BYOD onboarding NRE.' },
    ],
  },
  {
    title: 'BYOD format difficulty',
    fields: [
      { key: 'difficultyMultipliers.api', label: 'API', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for API / native-digital delivery.' },
      { key: 'difficultyMultipliers.spreadsheet', label: 'Spreadsheet', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for spreadsheet delivery.' },
      { key: 'difficultyMultipliers.scannedPdf', label: 'Scanned PDF', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for scanned-PDF delivery.' },
      { key: 'difficultyMultipliers.handwritten', label: 'Handwritten', nre: true, step: 0.1, tooltip: 'Onboarding difficulty for handwritten delivery.' },
    ],
  },
  {
    title: 'Floor',
    fields: [
      { key: 'moduleFloor', label: 'Module floor', tooltip: 'Minimum the module charges if the summed lines fall below it.' },
    ],
  },
  {
    title: 'Discount',
    fields: [
      { key: 'durationDiscountPct', label: 'Duration discount %', tooltip: 'Overrides the duration-band discount for this proposal. Applies to the reporting and algorithm lines.' },
    ],
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  config?: Record<string, number>;
  overrides: Record<string, number>;
  onOverrideChange: (key: string, value: number | undefined) => void;
  onResetAll: () => void;
  // The active nre config (setup hours, difficulty multipliers, etc.) that drives
  // the BYOD onboarding NRE.
  nreConfig?: Record<string, unknown>;
  // The duration-band discount percent currently in effect (from the last
  // Calculate Price), shown as the default for the discount override.
  discountDefault?: number;
}

const readNested = (source: Record<string, unknown> | undefined, key: string): number | undefined => {
  const value = key
    .split('.')
    .reduce<unknown>((acc, part) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[part] : undefined), source);
  return typeof value === 'number' ? value : undefined;
};

export const PipeFitterRatesDrawer: React.FC<Props> = ({
  open,
  onClose,
  config,
  overrides,
  onOverrideChange,
  onResetAll,
  nreConfig,
  discountDefault = 0,
}) => {
  if (!open) return null;

  // nre fields are overridden with an nre. prefix; everything else is flat.
  const overrideKeyOf = (field: RateField): string => (field.nre ? `nre.${field.key}` : field.key);

  const defaultValue = (field: RateField): number => {
    if (field.key === 'durationDiscountPct') return discountDefault;
    if (field.nre) {
      const lastPart = field.key.split('.').pop() ?? field.key;
      return readNested(nreConfig, field.key) ?? PIPEFITTER_NRE_FALLBACKS[field.key] ?? DIFFICULTY_FALLBACKS[lastPart] ?? 0;
    }
    return config?.[field.key] ?? PIPEFITTER_RATE_FALLBACKS[field.key] ?? 0;
  };

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="PipeFitter rates">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-[#E2E5EA] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold font-heading text-blueprint-blue">PipeFitter cost assumptions</h3>
            <p className="mt-0.5 text-xs text-heritage-grey">
              From the active configuration. Edit any value to override it for this proposal only.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-heritage-grey hover:text-blueprint-blue" aria-label="Close">
            &times;
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          {GROUPS.map((group) => (
            <div key={group.title}>
              <h4 className="text-sm font-medium text-blueprint-blue">{group.title}</h4>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-4">
                {group.fields.map((field) => {
                  const key = overrideKeyOf(field);
                  const override = overrides[key];
                  const isOverridden = override !== undefined;
                  const def = defaultValue(field);
                  const native = isOverridden ? override : def;
                  const shown = field.percent ? native * PERCENT : native;
                  return (
                    <div key={key}>
                      <DraftNumberInput
                        id={`pf-rate-${key}`}
                        label={field.label}
                        tooltip={field.tooltip}
                        step={field.step ?? 'any'}
                        value={shown}
                        className={isOverridden ? 'border-primary' : ''}
                        onValueChange={(raw) => {
                          if (raw === '') {
                            onOverrideChange(key, undefined);
                            return;
                          }
                          const n = Number(raw);
                          if (Number.isNaN(n)) return;
                          onOverrideChange(key, field.percent ? n / PERCENT : n);
                        }}
                      />
                      <div className="mt-0.5 flex items-center justify-between text-[11px] text-heritage-grey">
                        <span>default {field.percent ? `${def * PERCENT}%` : def}</span>
                        {isOverridden && (
                          <button type="button" className="text-primary hover:underline" onClick={() => onOverrideChange(key, undefined)}>
                            reset
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-[#E2E5EA] px-5 py-3">
          <button type="button" onClick={onResetAll} className="text-sm text-text-grey hover:text-blueprint-blue">
            Reset all to defaults
          </button>
          <button type="button" onClick={onClose} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
